<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useStore } from '@/store'
import { formatDate } from '../utils/dateFormatter'
import { degreeService } from '../services/degreeService'
import { getAuth } from 'firebase/auth'
import { ref as dbRef, onValue, off, update, get, remove, query, orderByChild, startAt, endAt, limitToFirst, type Query } from 'firebase/database'
import { database } from '@/firebase/config'
import FileUploader from '../components/FileUploader.vue'
import BaseModal from '../components/BaseModal.vue'
import type { IExtendedDegree, IExtendedDegreePackage, TSearchType } from '@shared/types/extensions'
import { EDocumentStatus, EAuthorizationType, EPeriodType, EDocumentType, ETipoError } from '@shared/types/enums'
import { downloadService } from '../services/downloadService'
import DegreeDetailsModal from '@/components/DegreeDetailsModal.vue'
import NewDegreeModal from '@/components/titulos/NewDegreeModal.vue'
import VirtualizedDegreesView from '@/components/titulos/VirtualizedDegreesView.vue'
import PackageView from '@/components/titulos/PackageView.vue'
import PackageDegreeModal from '@/components/titulos/PackageDegreeModal.vue'
import CleanErrorModal from '@/components/common/CleanErrorModal.vue'
import { es } from 'date-fns/locale/es'
import { VueDatePicker } from '@vuepic/vue-datepicker'
const store = useStore()
const auth = getAuth()
const db = database

// Variables principales
const activeTab = ref<'degrees' | 'packages' | 'search'>('degrees')
const degrees = ref<IExtendedDegree[]>([])
const packages = ref<IExtendedDegreePackage[]>([])
const loading = ref(false)

// Cache para optimizar el rendimiento
const degreesCache = new Map<string, IExtendedDegree>()
const lastLoadTime = ref<number>(0)
const searchType = ref<TSearchType>('folio')
const searchValue = ref('')
const searchResult = ref<IExtendedDegree[] | null>(null)
const showPackageModal = ref(false)
const showDegreeDetailsModal = ref(false)
const showEditModal = ref(false)
const selectedDegree = ref<IExtendedDegree | null>(null)
const editingDegree = ref<IExtendedDegree | null>(null)
const showErrorModal = ref(false)
const showNewDegreeModal = ref(false)
const showCleanErrorModal = ref(false)
const errorCount = ref(0)
const testMode = computed(() => store.getters.isTestMode)
const errorMessage = ref<string>('')
const selectedPackageIndex = ref<number | null>(null)
const packagesDate = ref(new Date().getFullYear())
let _packagesListenerQuery: Query | null = null
let cleanupUploadStatusListener: (() => void) | null = null
const institutionId = computed(() => store.getters['global/currentInstitution'])

// Computed properties for selected degrees
const selectedDegrees = computed(() => degrees.value.filter(d => d.selected === true))

const searchPlaceholder = computed(() => {
	switch (searchType.value) {
		case 'folio':
			return 'Folio de Control'
		case 'numero':
			return 'Número de Control'
		case 'curp':
			return 'CURP'
		default:
			return ''
	}
})

// Firebase paths
const getInstitutionPath = (path: string) => `institutions/${institutionId.value}${testMode.value ? '/testMode' : ''}${path}`

const getUserPath = (path: string) => `users/${auth.currentUser?.uid}${path}`

const formateDatePicker = (year: number) => {
	const month = 1 // 1-12
	const day = 1
	return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

const DATE_FORMAT = /^\d{4}-\d{2}-\d{2}$/
function getYearRange(dateStr: string): { start: number; end: number } {
	const trimmed = dateStr?.trim?.() ?? ''
	if (!DATE_FORMAT.test(trimmed)) {
		throw new TypeError('date debe tener formato YYYY-MM-DD')
	}
	const year = Number.parseInt(trimmed.slice(0, 4), 10)
	const startDate = new Date(Date.UTC(year, 0, 1, 0, 0, 0, 0))
	const endDate = new Date(Date.UTC(year, 11, 31, 23, 59, 59, 999))
	return { start: startDate.getTime(), end: endDate.getTime() }
}
// Tab selection
const selectTab = (tab: 'degrees' | 'packages' | 'search') => {
	if (activeTab.value === tab) return

	// Limpiar listeners de la pestaña anterior

	if (activeTab.value === 'degrees') {
		const degreesRef = dbRef(db, getInstitutionPath('/degrees/queue'))
		off(degreesRef)
	} else if (activeTab.value === 'packages') {
		if (_packagesListenerQuery) {
			off(_packagesListenerQuery)
			_packagesListenerQuery = null
		} else {
			const packagesRef = dbRef(db, getInstitutionPath('/degrees/packages'))
			off(packagesRef)
		}
	}

	activeTab.value = tab

	if (tab === 'degrees') {
		loadDegrees()
	} else if (tab === 'packages') {
		loadPackages(formateDatePicker(packagesDate.value))
	} else if (tab === 'search') {
		searchResult.value = null
	}
}

// Template download
const downloadTemplate = async () => {
	try {
		loading.value = true
		await degreeService.downloadTemplate(institutionId.value, 'degree', testMode.value)
		if ('Notification' in window) {
			new Notification('Descarga de plantilla', {
				body: 'Plantilla descargada exitosamente',
				icon: '/favicon.ico'
			})
		}
	} catch (error) {
		console.error('Error downloading template:', error)
		showErrorModal.value = true
		errorMessage.value = 'Error al descargar la plantilla'
	} finally {
		loading.value = false
	}
}

// Marcar processing en RTDB al iniciar la subida a Storage (antes del trigger del backend)
const handleUploadStart = async (startInfo: { fileName: string; path: string }) => {
	try {
		const uploadRef = dbRef(db, getUserPath('/userData/uploadDegree/'))
		await update(uploadRef, {
			status: 'uploading',
			errorsMessages: null,
			initTime: new Date().getTime(),
			filePath: startInfo.path,
			fileName: startInfo.fileName
		})
	} catch (error) {
		console.error('Error al iniciar estado de subida:', error)
		if (error instanceof Error) {
			store.dispatch('loader/setError', error.message)
		}
	}
}

// Handle upload complete from FileUploader
const handleUploadComplete = async (uploadResult: { url: string; fileName: string; fileType: string; path: string }) => {
	try {
		const uploadRef = dbRef(db, getUserPath('/userData/uploadDegree/'))
		const snapshot = await get(uploadRef)
		const current = snapshot.val()

		// Evitar race: el backend puede haber terminado (remove) o reportado error antes de este callback
		if (!current || current.status === 'error' || current.status === 'timeout' || current.status === 'complete') {
			selectTab('degrees')
			return
		}

		// Backend aún no terminó: refrescar initTime desde que el archivo ya está en Storage
		await update(uploadRef, {
			status: 'uploading',
			errorsMessages: null,
			initTime: new Date().getTime(),
			filePath: uploadResult.path,
			fileName: uploadResult.fileName
		})

		selectTab('degrees')
	} catch (error) {
		console.error('Error al procesar la subida:', error)
		if (error instanceof Error) {
			store.dispatch('loader/setError', error.message)
		}
	}
}

// Handle upload error from FileUploader
const handleUploadError = async (error: Error) => {
	console.error('Error en la subida:', error)
	store.dispatch('loader/setError', error.message)
	try {
		const uploadRef = dbRef(db, getUserPath('/userData/uploadDegree/'))
		await update(uploadRef, {
			status: 'error',
			errorsMessages: [{ message: error.message || 'Error al subir el archivo' }]
		})
	} catch (updateError) {
		console.error('Error al actualizar estado de subida tras fallo:', updateError)
	}
}

// Handle upload progress from FileUploader
const handleUploadProgress = (progress: number) => {
	store.dispatch('loader/updateProgress', progress)
}

// Handle degree removed
const handleDegreeRemoved = () => {
	// Usar la nueva función que mantiene las selecciones
	updateDegreesAfterOperation()
}

// Manejar título guardado exitosamente
const handleDegreeSaved = () => {
	// En lugar de forzar recarga completa, solo actualizar el caché y mantener selecciones
	updateDegreesAfterOperation()
	selectedDegree.value = null
}

// Manejar título actualizado exitosamente
const handleDegreeUpdated = () => {
	// En lugar de forzar recarga completa, solo actualizar el caché y mantener selecciones
	updateDegreesAfterOperation()
}

// Función para actualizar títulos después de operaciones manteniendo selecciones
const updateDegreesAfterOperation = () => {
	// Limpiar caché para forzar actualización de datos
	degreesCache.clear()
	lastLoadTime.value = 0

	// Recargar datos manteniendo las selecciones actuales
	const currentSelections = new Map(degrees.value.map(d => [d.id, d.selected]))

	// Recargar datos
	loadDegrees()

	// Restaurar selecciones después de la recarga
	setTimeout(() => {
		degrees.value.forEach(degree => {
			if (currentSelections.has(degree.id)) {
				degree.selected = currentSelections.get(degree.id) || false
			}
		})
	}, 100) // Pequeño delay para asegurar que los datos se han cargado
}

// Submit edit
const submitEdit = async () => {
	if (!editingDegree.value) return

	try {
		loading.value = true
		const degreeRef = dbRef(db, getInstitutionPath(`/degrees/queue/${editingDegree.value.id}`))
		await update(degreeRef, {
			data: editingDegree.value.data
		})
		showEditModal.value = false
	} catch (error) {
		console.error('Error updating degree:', error)
		showErrorModal.value = true
		errorMessage.value = 'Error al actualizar el título'
	} finally {
		loading.value = false
	}
}

// Remove selected degrees
const removeSelectedDegrees = async () => {
	if (selectedDegrees.value.length === 0) {
		showErrorModal.value = true
		errorMessage.value = 'Debe seleccionar al menos 1 registro'
		return
	}

	if (!confirm(`¿Borrar ${selectedDegrees.value.length} títulos?`)) return

	try {
		loading.value = true
		const updates: Record<string, null> = {}
		selectedDegrees.value.forEach(degree => {
			updates[getInstitutionPath(`/degrees/queue/${degree.id}`)] = null
		})
		await update(dbRef(db), updates)
	} catch (error) {
		console.error('Error removing degrees:', error)
		showErrorModal.value = true
		errorMessage.value = 'Error al eliminar los títulos'
	} finally {
		loading.value = false
	}
}
const selectAllDegrees = () => {
	degrees.value.forEach(degree => {
		degree.selected = true
	})
}
const unselectAllDegrees = () => {
	degrees.value.forEach(degree => {
		degree.selected = false
	})
}
const removeAllDegreesWithErrors = async () => {
	const degreesWithErrors = degrees.value.filter(degree => degree.error)

	if (degreesWithErrors.length === 0) {
		showErrorModal.value = true
		errorMessage.value = 'No hay títulos con errores para eliminar'
		return
	}

	if (!confirm(`¿Está seguro de eliminar ${degreesWithErrors.length} título(s) con errores?`)) return

	try {
		loading.value = true
		const promises = degreesWithErrors.map(async degree => {
			try {
				const degreeRef = dbRef(db, getInstitutionPath(`/degrees/queue/${degree.id}`))
				await remove(degreeRef)
			} catch (error) {
				console.error(`Error al eliminar título ${degree.id}:`, error)
				throw error
			}
		})

		await Promise.all(promises)
		await loadDegrees()
	} catch (error) {
		console.error('Error removing degrees:', error)
		showErrorModal.value = true
		errorMessage.value = 'Error al eliminar los títulos. Por favor, intente nuevamente.'
	} finally {
		loading.value = false
	}
}

// Función ultra-optimizada para crear objetos Degree
const createDegreeObjectOptimized = (id: string, degreeData: any, selected: boolean = false): IExtendedDegree => {
	// Optimización: Acceso directo a propiedades sin crear variables intermedias
	const data = degreeData.data || {}
	const prof = data.Profesionista || {}
	const carr = data.Carrera || {}

	// Optimización: Pre-calcular el nombre del estudiante una sola vez
	const studentName = `${prof.nombre || ''} ${prof.primerApellido || ''} ${prof.segundoApellido || ''}`.trim()

	// Optimización: Pre-calcular la fecha una sola vez
	const date = degreeData.date ? new Date(degreeData.date).getTime() : Date.now()

	return {
		id,
		type: EDocumentType.DEGREE,
		data: {
			Profesionista: {
				nombre: prof.nombre || '',
				primerApellido: prof.primerApellido || '',
				segundoApellido: prof.segundoApellido || '',
				curp: prof.curp || '',
				correoElectronico: prof.correoElectronico || ''
			},
			Carrera: {
				nombreCarrera: carr.nombreCarrera || '',
				idCarrera: carr.idCarrera || '',
				cveCarrera: carr.claveCarrera || '',
				clavePlan: carr.clavePlan || '',
				idNivelEstudios: carr.idNivelEstudios || 0,
				autorizacionReconocimiento: carr.autorizacionReconocimiento || '',
				fechaInicio: carr.fechaInicio || '',
				fechaTerminacion: carr.fechaTerminacion || '',
				idAutorizacionReconocimiento: carr.idAutorizacionReconocimiento || EAuthorizationType.RVOE_FEDERAL,
				idTipoPeriodo: carr.idTipoPeriodo || EPeriodType.SEMESTRE,
				calificacionMaxima: carr.calificacionMaxima || 10,
				calificacionMinima: carr.calificacionMinima || 5,
				calificacionMinimaAprobatoria: carr.calificacionMinimaAprobatoria || '6.00',
				tipoPeriodo: carr.tipoPeriodo || 'SEMESTRE',
				numeroRvoe: carr.numeroRvoe || ''
			},
			Antecedente: data.Antecedente || {},
			Expedicion: data.Expedicion || {},
			Institucion: data.Institucion || {},
			TituloElectronico: data.TituloElectronico || {},
			numeroControl: data.numeroControl || '',
			insertTime: data.insertTime || Date.now(),
			row: data.row || 0,
			FirmaResponsables: data.FirmaResponsables || {}
		},
		controlNumber: degreeData.controlNumber || '',
		date,
		status: degreeData.status || EDocumentStatus.PENDING,
		warning: degreeData.warning,
		error: degreeData.error,
		selected,
		folio: degreeData.folio || '',
		folioControl: degreeData.folioControl,
		xml: degreeData.xml,
		cadenaOriginal: degreeData.cadenaOriginal,
		success: degreeData.success || false,
		studentName,
		career: carr.nombreCarrera,
		package: degreeData.package,
		xmlAuthenticated: ''
	}
}

// Download selected degrees
const downloadSelectedDegrees = async () => {
	if (selectedDegrees.value.length === 0) {
		showErrorModal.value = true
		errorMessage.value = 'Debe seleccionar al menos 1 título'
		return
	}

	try {
		loading.value = true

		for (const degree of selectedDegrees.value) {
			const degreeRef = dbRef(db, getInstitutionPath(`/degrees/queue/${degree.id}`))
			const snapshot = await get(degreeRef)

			if (snapshot.exists()) {
				const degreeData = snapshot.val()
				const degreeToDownload = createDegreeObjectOptimized(degree.id, degreeData)
				await downloadService.downloadDocument(degreeToDownload, 'título')
			}
		}
	} catch (error) {
		console.error('Error downloading selected degrees:', error)
		showErrorModal.value = true
		errorMessage.value = 'No fue posible descargar los títulos seleccionados'
	} finally {
		loading.value = false
	}
}

// Create package
const createPackage = () => {
	if (selectedDegrees.value.length === 0) {
		showErrorModal.value = true
		errorMessage.value = 'Debe seleccionar al menos 1 registro'
		return
	}
	showPackageModal.value = true
}

// Perform search
const performSearch = async () => {
	if (!searchValue.value || searchValue.value.length < 4) {
		showErrorModal.value = true
		errorMessage.value = 'El valor de la búsqueda debe tener al menos 4 caracteres'
		return
	}

	try {
		loading.value = true
		searchResult.value = null
		const results = await degreeService.searchDegrees(searchType.value, searchValue.value, institutionId.value, testMode.value)

		searchResult.value = results
	} catch (error) {
		console.error('Error searching degrees:', error)
		showErrorModal.value = true
		errorMessage.value = 'Error al consultar los datos'
	} finally {
		loading.value = false
	}
}

// View degree details
const viewDegreeDetails = async (id: string) => {
	try {
		loading.value = true
		const degreeRef = dbRef(db, getInstitutionPath(`/degrees/queue/${id}`))
		const snapshot = await get(degreeRef)

		if (snapshot.exists()) {
			const degreeData = snapshot.val()
			selectedDegree.value = createDegreeObjectOptimized(id, degreeData)
			showDegreeDetailsModal.value = true
		}
	} catch (error) {
		console.error('Error loading degree details:', error)
		showErrorModal.value = true
		errorMessage.value = 'Error al cargar los detalles del título'
	} finally {
		loading.value = false
	}
}

// Load degrees with performance optimizations
const loadDegrees = () => {
	loading.value = true
	const degreesRef = dbRef(db, getInstitutionPath('/degrees/queue'))

	off(degreesRef)

	onValue(
		degreesRef,
		snapshot => {
			if (snapshot.exists()) {
				const allDegreesData = snapshot.val()

				// Optimización 1: Usar requestAnimationFrame para procesar en chunks
				requestAnimationFrame(() => {
					processDegreesData(allDegreesData)
				})
			} else {
				degrees.value = []
				loading.value = false
			}
		},
		error => {
			console.error('Error loading degrees with onValue:', error)
			// Verificar si el error es debido a una desconexión por logout
			// No mostrar alerta si el usuario se desconectó
			const firebaseError = error as any
			if (!firebaseError.hasOwnProperty('code') || (firebaseError.code !== ETipoError.PERMISSION_DENIED && firebaseError.code !== ETipoError.UNAUTHENTICATED)) {
				showErrorModal.value = true
				errorMessage.value = 'Error al cargar los títulos.'
			}
			loading.value = false
			degrees.value = []
		}
	)
}

// Función ultra-optimizada para procesar datos de títulos con mejor rendimiento
const processDegreesData = (allDegreesData: any) => {
	const entries = Object.entries(allDegreesData)
	const chunkSize = 25 // Reducir el tamaño del chunk para mejor responsividad
	const filteredDegrees: IExtendedDegree[] = []
	const currentTime = Date.now()

	// Verificar si podemos usar el caché - reducir la duración del caché para actualizaciones más frecuentes
	const canUseCache = currentTime - lastLoadTime.value < 5000 && degreesCache.size > 0 // Reducir de 30 segundos a 5 segundos

	// Crear un Map para búsquedas más rápidas de títulos existentes y sus selecciones
	const currentSelections = new Map(degrees.value.map(d => [d.id, d.selected]))

	// Función para procesar un chunk con mejor manejo de memoria
	const processChunk = (startIndex: number) => {
		const endIndex = Math.min(startIndex + chunkSize, entries.length)

		for (let i = startIndex; i < endIndex; i++) {
			const [id, data] = entries[i]

			// Optimización: Filtrar antes de procesar
			if ((data as any).package === undefined || (data as any).package === null) {
				let degree: IExtendedDegree

				// Optimización: Usar caché si está disponible
				if (canUseCache && degreesCache.has(id)) {
					degree = degreesCache.get(id)!
					// Actualizar la selección y las propiedades reactivas (error, warning)
					degree.selected = currentSelections.get(id) || false
					// Siempre actualizar error y warning para mantener reactividad
					degree.error = (data as any).error
					degree.warning = (data as any).warning
					degree.success = (data as any).success || false
				} else {
					// Crear nuevo objeto y guardar en caché
					degree = createDegreeObjectOptimized(id, data, currentSelections.get(id) || false)
					degreesCache.set(id, degree)
				}

				filteredDegrees.push(degree)
			}
		}

		// Si hay más chunks para procesar, continuar en el siguiente frame con delay
		if (endIndex < entries.length) {
			setTimeout(() => processChunk(endIndex), 0) // Usar setTimeout en lugar de requestAnimationFrame para mejor rendimiento
		} else {
			// Optimización: Ordenar solo al final y actualizar de forma asíncrona
			setTimeout(() => {
				degrees.value = filteredDegrees.sort((a, b) => {
					const dateA = new Date(b.date)?.getTime() || 0
					const dateB = new Date(a.date)?.getTime() || 0
					return dateA - dateB
				})
				lastLoadTime.value = currentTime
				loading.value = false
			}, 0)
		}
	}

	// Iniciar el procesamiento
	processChunk(0)
}

// Wrapper function for loadPackages with logging
const handleRefreshPackages = async () => {
	await loadPackages(formateDatePicker(packagesDate.value))
}

// Load packages
const loadPackages = async (date: string) => {
	const packagesRef = dbRef(db, getInstitutionPath('/degrees/packages'))

	// +++ limpiar listener anterior (ref o query)
	if (_packagesListenerQuery) {
		off(_packagesListenerQuery)
		_packagesListenerQuery = null
	} else {
		off(packagesRef)
	}

	// +++ probe: si hay >= 26 paquetes, aplicamos filtro por año en servidor
	const probeSnapshot = await get(query(packagesRef, limitToFirst(26)))
	const probeValue = probeSnapshot.val() as Record<string, unknown> | null
	const useYearFilter = probeValue ? Object.keys(probeValue).length > 25 : false
	const { start, end } = getYearRange(date)

	let packagesQuery: Query = packagesRef
	if (useYearFilter) {
		packagesQuery = query(packagesRef, orderByChild('id'), startAt(start), endAt(end))
	}

	_packagesListenerQuery = packagesQuery
	onValue(
		packagesQuery,
		snapshot => {
			const data = snapshot.val()
			if (data) {
				const entries = Object.entries(data)
				const filteredEntries = useYearFilter
					? entries
					: entries.filter(([id, packageData]) => {
							const raw = (packageData as Record<string, unknown>).id ?? id
							const value = typeof raw === 'number' ? raw : Number(raw)
							return Number.isFinite(value) && value >= start && value <= end
						})
				const processedPackages = filteredEntries.map(([id, packageData]) => ({
					id: Number(id),
					date: (packageData as any).id || '',
					signed: (packageData as any).sign || false,
					sent: (packageData as any).sent || false,
					numDegrees: (packageData as any).numDegrees || 0,
					responsables: (packageData as any).responsables || {},
					label: (packageData as any).label || '',
					degrees: (packageData as any).degrees || {},
					folioControl: (packageData as any).folioControl || {},
					procesedBySep: (packageData as any).procesedBySep || false,
					sepResult: (packageData as any).sepResult || [],
					timeToSentSep: (packageData as any).timeToSentSep || null,
					sign: (packageData as any).sign || false
				}))
				packages.value = processedPackages
			} else {
				packages.value = []
			}
		},
		error => {
			console.error('Error loading packages with onValue:', error)
			// Verificar si el error es debido a una desconexión por logout
			// No mostrar alerta si el usuario se desconectó
			const firebaseError = error as any
			if (!firebaseError.hasOwnProperty('code') || (firebaseError.code !== ETipoError.PERMISSION_DENIED && firebaseError.code !== ETipoError.UNAUTHENTICATED)) {
				store.dispatch('notification/showError', 'Error al cargar los paquetes.')
			}
			packages.value = []
		}
	)
}

// Watch for tab changes
watch(activeTab, async newTab => {
	if (newTab === 'degrees') {
		loadDegrees()
	} else if (newTab === 'packages') {
		await loadPackages(formateDatePicker(packagesDate.value))
	}
})

// Watch for institution ID changes
watch(institutionId, async (newId, oldId) => {
	if (newId !== oldId) {
		degrees.value = []
		searchResult.value = null
		searchValue.value = ''

		if (newId) {
			if (activeTab.value === 'degrees') {
				loadDegrees()
			} else if (activeTab.value === 'packages') {
				await loadPackages(formateDatePicker(packagesDate.value))
			}
		}
	}
})

// Watch for test mode changes
watch(testMode, async (newMode, oldMode) => {
	if (newMode !== oldMode) {
		// Limpiar datos actuales
		degrees.value = []
		packages.value = []
		searchResult.value = null
		searchValue.value = ''

		// Recargar datos según la pestaña activa
		if (activeTab.value === 'degrees') {
			loadDegrees()
		} else if (activeTab.value === 'packages') {
			await loadPackages(formateDatePicker(packagesDate.value))
		}
	}
})

// Watch para mantener selecciones sincronizadas
watch(
	degrees,
	newDegrees => {
		// Asegurar que las propiedades selected sean reactivas
		newDegrees.forEach(degree => {
			if (degree.selected === undefined) {
				degree.selected = false
			}
		})
	},
	{ deep: true }
)
watch(packagesDate, async (newDate, oldDate) => {
	if (newDate !== oldDate) {
		if (newDate !== null) {
			await loadPackages(formateDatePicker(newDate))
		}
	}
})

// Initialize
onMounted(async () => {
	if (!institutionId.value) {
		try {
			await store.dispatch('global/loadInstitutions', null, { root: true })

			if (!institutionId.value) {
				showErrorModal.value = true
				errorMessage.value = 'No se pudo determinar la institución. Por favor, seleccione una institución.'
				return
			}
		} catch (error) {
			console.error('Error loading institutions:', error)
			showErrorModal.value = true
			errorMessage.value = 'Error al cargar las instituciones. Por favor, intente nuevamente.'
			return
		}
	}

	loadDegrees()
	cleanupUploadStatusListener = setupUploadStatusListener()
})

onUnmounted(() => {
	const degreesRef = dbRef(db, getInstitutionPath('/degrees/queue'))
	off(degreesRef)

	if (_packagesListenerQuery) {
		off(_packagesListenerQuery)
		_packagesListenerQuery = null
	} else {
		const packagesRef = dbRef(db, getInstitutionPath('/degrees/packages'))
		off(packagesRef)
	}

	if (cleanupUploadStatusListener) {
		cleanupUploadStatusListener()
		cleanupUploadStatusListener = null
	}
})

// Watch for upload status changes with extended timeout for degrees
const PROCESSING_TIMEOUT_MS = 300000 // 5 minutos (alineado con timeoutSeconds del trigger de Storage)

const setupUploadStatusListener = () => {
	const user = auth.currentUser
	if (!user) {
		console.error('Usuario no autenticado, no se puede configurar el listener de subida.')
		return () => {
			console.log('Limpieza de listener de estado de subida.')
		}
	}

	const uploadStatusRef = dbRef(db, `users/${user.uid}/userData/uploadDegree`)
	let unsubscribe: (() => void) | null = null
	let timeoutId: number | null = null
	let isProcessing = false

	const clearProcessingTimeout = () => {
		if (timeoutId) {
			clearTimeout(timeoutId)
			timeoutId = null
		}
	}

	const markTimeoutInFirebase = async () => {
		try {
			await update(uploadStatusRef, {
				status: 'timeout',
				errorsMessages: [
					{
						message: 'El procesamiento de títulos excedió el tiempo máximo de espera'
					}
				]
			})
		} catch (error) {
			console.error('Error al marcar timeout en Firebase:', error)
			showErrorModal.value = true
			errorMessage.value =
				'El procesamiento de títulos tardó más de 5 minutos. Esto puede deberse a un archivo muy grande o problemas de conexión. Por favor, intente con un archivo más pequeño o contacte al administrador.'
			store.dispatch('loader/setLoading', { isLoading: false })
			store.dispatch('loader/cancelUpload')
			isProcessing = false
		}
	}

	const scheduleProcessingTimeout = (initTime: number) => {
		clearProcessingTimeout()
		const now = Date.now()
		const elapsed = initTime ? now - initTime : 0
		const remaining = Math.max(0, PROCESSING_TIMEOUT_MS - elapsed)

		timeoutId = window.setTimeout(() => {
			if (!isProcessing) return
			console.error('Tiempo de espera excedido para títulos:', elapsed, 'ms')
			void markTimeoutInFirebase()
		}, remaining)
	}

	const listenerCallback = (snapshot: any) => {
		const uploadData = snapshot.val()

		if (uploadData === null) {
			store.dispatch('loader/setLoading', { isLoading: false })
			store.dispatch('loader/cancelUpload')
			clearProcessingTimeout()
			isProcessing = false
			return
		}

		const status = uploadData.status
		const errors = uploadData.errorsMessages

		switch (status) {
			case 'uploading': {
				isProcessing = true
				const now = Date.now()
				const initTime = uploadData.initTime || 0

				if (initTime && now - initTime > PROCESSING_TIMEOUT_MS) {
					console.error('Tiempo de espera excedido al iniciar:', now - initTime, 'ms')
					void markTimeoutInFirebase()
					break
				}

				scheduleProcessingTimeout(initTime)
				store.dispatch('loader/setLoading', {
					isLoading: true,
					message: 'Procesando títulos... (puede tardar varios minutos para archivos grandes)'
				})
				break
			}
			case 'complete':
				isProcessing = false
				clearProcessingTimeout()
				store.dispatch('loader/setLoading', { isLoading: false })
				store.dispatch('loader/cancelUpload')
				break
			case 'error': {
				isProcessing = false
				clearProcessingTimeout()

				let formattedErrors
				if (Array.isArray(errors)) {
					formattedErrors = errors.map(error => {
						if (typeof error === 'object' && error !== null) {
							return {
								row: error.row || '',
								message: error.message || '',
								data: error.data || null
							}
						}
						return {
							message: error.toString()
						}
					})
				} else if (typeof errors === 'object' && errors !== null) {
					formattedErrors = [
						{
							message: errors.message || JSON.stringify(errors)
						}
					]
				} else {
					formattedErrors = [
						{
							message: errors?.toString() || 'Error desconocido en la carga del archivo'
						}
					]
				}
				store.dispatch('notification/showError', formattedErrors)
				store.dispatch('loader/setLoading', { isLoading: false })
				store.dispatch('loader/cancelUpload')
				showCleanErrorModal.value = true
				errorCount.value = formattedErrors.length
				break
			}
			case 'timeout':
				isProcessing = false
				clearProcessingTimeout()
				showErrorModal.value = true
				errorMessage.value =
					'El procesamiento de títulos tardó más de 5 minutos. Esto puede deberse a un archivo muy grande o problemas de conexión. Por favor, intente con un archivo más pequeño o contacte al administrador.'
				store.dispatch('loader/setLoading', { isLoading: false })
				store.dispatch('loader/cancelUpload')
				break
			default:
				isProcessing = false
				clearProcessingTimeout()
				store.dispatch('loader/setLoading', { isLoading: false })
				store.dispatch('loader/cancelUpload')
				break
		}
	}

	const errorCallback = (error: Error) => {
		console.error('Error en el listener de estado de subida:', error)
		isProcessing = false
		clearProcessingTimeout()
		store.dispatch('loader/setLoading', { isLoading: false })
		store.dispatch('loader/cancelUpload')
	}

	unsubscribe = onValue(uploadStatusRef, listenerCallback, errorCallback)

	return () => {
		if (unsubscribe) {
			unsubscribe()
			unsubscribe = null
		}
		clearProcessingTimeout()
		isProcessing = false
	}
}
watch(institutionId, async (newId, oldId) => {
	if (newId !== oldId) {
		loadDegrees()
		await loadPackages(formateDatePicker(packagesDate.value))
	}
})

const cleanErrors = async () => {
	await store.dispatch('cleanUploadDegree').then(() => {
		showCleanErrorModal.value = false
		loadDegrees()
	})
}
</script>
<template>
	<div class="page-container">
		<!-- Header Section -->
		<div class="mb-3">
			<div class="degree-page-header">
				<h2 class="degree-page-header__title">Generación y Firmado de Títulos</h2>
				<div class="degree-page-header__actions">
					<VaButton class="degree-page-header__action" color="primary" icon="download" @click="downloadTemplate"> Descargar Plantilla</VaButton>
					<div class="degree-page-header__action degree-page-header__uploader">
						<FileUploader
							@upload-start="handleUploadStart"
							@upload-complete="handleUploadComplete"
							@upload-error="handleUploadError"
							@upload-progress="handleUploadProgress"
							buttonText="Subir Excel"
							acceptTypes=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
							:disabled="false"
							type="degree"
							buttonClass="btn btn-success"
							iconClass="fas fa-upload"
							:storagePath="`files/institutions/${institutionId}/${auth.currentUser?.uid}/degrees`"
							:file-name="'fileMassiveDegrees.xlsx'"
							:institution-id="institutionId"
							:test-mode="testMode" />
					</div>
					<VaButton class="degree-page-header__action" color="secondary" icon="add" @click="showNewDegreeModal = true">Nuevo Título</VaButton>
				</div>
			</div>
		</div>

		<div class="row">
			<div class="col-md-4">
				<!-- Tabs Section -->
				<VaTabs v-model="activeTab" color="primary">
					<template #tabs>
						<VaTab :class="[{ active: activeTab === 'degrees' }]" icon="task" label="Títulos sin paquete" name="degrees" @click="selectTab('degrees')" />
						<VaTab :class="[{ active: activeTab === 'packages' }]" icon="inventory_2" label="Paquetes" name="packages" @click="selectTab('packages')" />
						<VaTab :class="[{ active: activeTab === 'search' }]" icon="search" label="Búsqueda" name="search" @click="selectTab('search')" />
					</template>
				</VaTabs>
			</div>
			<div class="col-md-8">
				<div v-if="degrees.length > 0">
					<div class="counter-card">
						<div class="counter-item error">
							<VaIcon name="warning" class="counter-icon error" />
							<div class="counter-content">
								<span style="color: red; font-size: 10px">El limite por plantilla es de 300 títulos</span>
							</div>
						</div>
						<div class="counter-item">
							<VaIcon name="school" class="counter-icon total" />
							<div class="counter-content">
								<span class="counter-number">{{ degrees.length }}</span>
								<span class="counter-label">Total de Títulos</span>
							</div>
						</div>
						<div class="counter-item">
							<VaIcon name="check_circle" class="counter-icon selected" />
							<div class="counter-content">
								<span class="counter-number">{{ selectedDegrees.length }}</span>
								<span class="counter-label">Seleccionados</span>
							</div>
						</div>
						<div class="counter-item">
							<VaIcon name="error" class="counter-icon error" />
							<div class="counter-content">
								<span class="counter-number">{{ degrees.filter(d => d.error).length }}</span>
								<span class="counter-label">Con Errores</span>
							</div>
						</div>
						<div class="counter-item">
							<VaIcon name="pending" class="counter-icon pending" />
							<div class="counter-content">
								<span class="counter-number">{{ degrees.filter(d => d.warning).length }}</span>
								<span class="counter-label">Advertencias</span>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<!-- Content Section -->
		<div class="content-section">
			<!-- Degrees Tab -->
			<div v-if="activeTab === 'degrees'" class="tab-content">
				<!-- Contador de Títulos -->

				<div v-if="degrees.length > 0" class="action-buttons-container mb-3">
					<VaButton color="success" icon="compress" @click="createPackage">Empaquetar Seleccionados</VaButton>
					<VaButton color="warning" icon="cancel" @click="unselectAllDegrees">Quitar Seleccionados</VaButton>
					<VaButton color="primary" icon="add" @click="selectAllDegrees">Seleccionar Todos</VaButton>
					<VaButton color="info" icon="download" @click="downloadSelectedDegrees">Descargar Seleccionados</VaButton>
					<VaButton color="danger" icon="delete" @click="removeSelectedDegrees">Eliminar Seleccionados</VaButton>
					<VaPopover message="Este proceso eliminará todos los títulos con errores sin necesidad de seleccionarlos">
						<VaButton color="danger" icon="delete" @click="removeAllDegreesWithErrors">Eliminar Todos (Títulos con errores)</VaButton>
					</VaPopover>
				</div>
				<!-- Virtualized View -->
				<VirtualizedDegreesView
					v-if="degrees.length > 0"
					:degrees="degrees"
					:institution="store.state.global.selectedInstitution"
					:currentInstitution="institutionId"
					:testMode="testMode"
					@degree-removed="handleDegreeRemoved" />
				<div v-else class="empty-state">
					<img src="../assets/icons/title.svg" alt="No degrees" class="empty-icon" />
					<p class="empty-title">Aún no has creado títulos</p>
					<p class="empty-description">Crea un nuevo título o importa varios desde un archivo de excel</p>
				</div>
			</div>

			<!-- Packages Tab -->
			<div v-if="activeTab === 'packages'" class="packages-tab-container">
				<div v-if="packages.length > 0" class="packages-content">
					<div class="packages-year-filter">
						<span class="packages-year-label">Filtrar por año</span>
						<VueDatePicker v-model="packagesDate" :formats="{ input: 'yyyy' }" :locale="es" year-picker auto-apply class="packages-year-picker" :year-range="[2018, 2100]" />
						<VaPopover message="Hasta 25 paquetes: se muestran todos. Más de 25: se aplica filtro por año." placement="right">
							<button type="button" class="packages-year-info" aria-label="Información del filtro">
								<VaIcon name="info" size="small" />
							</button>
						</VaPopover>
					</div>
					<PackageView
						:packages="packages"
						:institution-id="institutionId"
						:test-mode="testMode"
						@package-removed="loadPackages(formateDatePicker(packagesDate))"
						@refresh-packages="handleRefreshPackages" />
				</div>
				<div v-else class="empty-state">
					<div class="packages-year-filter">
						<span class="packages-year-label">Filtrar por año</span>
						<VueDatePicker v-model="packagesDate" :formats="{ input: 'yyyy' }" :locale="es" year-picker auto-apply class="packages-year-picker" :year-range="[2018, 2100]" />
						<VaPopover message="Hasta 25 paquetes: se muestran todos. Más de 25: se aplica filtro por año." placement="right">
							<button type="button" class="packages-year-info" aria-label="Información del filtro">
								<VaIcon name="info" size="small" />
							</button>
						</VaPopover>
					</div>
					<img src="../assets/icons/emptyBox.svg" alt="No packages" class="empty-icon" />
					<p class="empty-title">Aún no has creado un paquete</p>
					<p class="empty-description">
						Organiza tus títulos en un paquete y ponle un nombre descriptivo, por ejemplo:
						<span class="text-primary">"Generación Contaduría del 2018"</span>
					</p>
				</div>
			</div>

			<!-- Search Tab -->
			<div v-if="activeTab === 'search'" class="search-section">
				<!-- Formulario de Búsqueda -->
				<div class="search-form">
					<div class="search-controls">
						<div class="search-select">
							<label for="searchType" class="search-label">Buscar título por:</label>
							<select id="searchType" v-model="searchType" class="form-control search-select-input">
								<option value="folio">Folio de Control</option>
								<option value="numero">Número de Control</option>
								<option value="curp">CURP</option>
							</select>
						</div>
						<div class="search-input-group">
							<input type="text" v-model="searchValue" :placeholder="'Ingresa el ' + searchPlaceholder.toLowerCase()" @keyup.enter="performSearch" class="form-control search-input" />
							<button class="btn btn-primary search-button" @click="performSearch"><i class="fas fa-search"></i> Buscar</button>
						</div>
					</div>
				</div>

				<!-- Resultados de Búsqueda -->
				<div v-if="searchResult !== null && searchResult.length > 0" class="search-results">
					<div class="results-header">
						<VaIcon name="list_alt" class="results-icon" />
						<span class="results-title">Resultados de Búsqueda ({{ searchResult.length }})</span>
					</div>
					<div class="results-grid">
						<div v-for="degree in searchResult" :key="degree.id" class="degree-details">
							<!-- Header del Título -->
							<div class="degree-header">
								<div class="header-content">
									<div class="student-info">
										<div class="student-avatar">
											<VaIcon name="person" class="student-icon" />
										</div>
										<div class="student-details">
											<h3 class="student-name">{{ degree.data.Profesionista.nombre }} {{ degree.data.Profesionista.primerApellido }} {{ degree.data.Profesionista.segundoApellido }}</h3>
											<p class="student-career">{{ degree.career || 'Carrera no especificada' }}</p>
										</div>
									</div>
									<div class="degree-actions">
										<VaPopover message="Ver detalles completos del título">
											<VaButton round icon="visibility" color="#0056b3" type="button" @click="viewDegreeDetails(degree.id)" style="display: flex; align-items: center; justify-content: center" />
										</VaPopover>
									</div>
								</div>
							</div>

							<!-- Contenido del Título -->
							<div class="degree-content">
								<div class="degree-info">
									<div v-if="degree.controlNumber" class="info-item">
										<VaIcon name="fingerprint" class="info-icon" />
										<span class="info-label">Número de Control:</span>
										<span class="info-value">{{ degree.controlNumber }}</span>
									</div>
									<div v-if="degree.data?.Profesionista?.curp" class="info-item">
										<VaIcon name="badge" class="info-icon" />
										<span class="info-label">CURP:</span>
										<span class="info-value">{{ degree.data.Profesionista.curp }}</span>
									</div>
									<div v-if="degree.date" class="info-item">
										<VaIcon name="schedule" class="info-icon" />
										<span class="info-label">Fecha de Registro:</span>
										<span class="info-value">{{ formatDate(degree.date, 'YYYY-MM-DD HH:mm') }}</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Estado Inicial -->
				<div v-else-if="searchResult === null" class="initial-state">
					<div class="empty-state">
						<VaIcon name="search" class="empty-icon" />
						<p class="empty-title">Busca Títulos</p>
						<p class="empty-description">Selecciona un criterio de búsqueda e ingresa el valor correspondiente</p>
					</div>
				</div>

				<!-- Sin Resultados -->
				<div v-else class="empty-state">
					<p class="empty-title">No se encontraron resultados</p>
					<p class="empty-description">Intente con otro criterio de búsqueda</p>
				</div>
			</div>
		</div>
		<!-- Modals -->
		<NewDegreeModal
			v-model="showNewDegreeModal"
			:isTestMode="testMode"
			:institution="store.state.global.selectedInstitution"
			:currentInstitution="institutionId"
			:isEdit="false"
			@degree-saved="handleDegreeSaved"
			@degree-updated="handleDegreeUpdated" />
		<PackageDegreeModal
			v-model="showPackageModal"
			:isTestMode="testMode"
			:institutionId="institutionId"
			:selectedDegrees="selectedDegrees"
			@update:packageView="loadPackages(formateDatePicker(packagesDate))"
			@update:modelValue="showPackageModal = $event" />

		<!-- Modal de Firma -->

		<!-- Modal de Edición -->
		<BaseModal v-model="showEditModal" title="Editar Título" :test-mode="testMode" message="Editar Título" size="medium">
			<template #body>
				<div v-if="editingDegree" class="mb-3">
					<label class="block mb-2 font-medium" for="numeroControl">Matrícula:</label>
					<input type="text" v-model="editingDegree.data.numeroControl" class="form-control" required />
				</div>
				<div v-if="editingDegree?.data?.Profesionista" class="mb-3">
					<label class="block mb-2 font-medium" for="nombre">Nombre:</label>
					<input type="text" v-model="editingDegree.data.Profesionista.nombre" class="form-control" required />
				</div>
				<div v-if="editingDegree?.data?.Profesionista" class="mb-3">
					<label class="block mb-2 font-medium" for="primerApellido">Primer Apellido:</label>
					<input type="text" v-model="editingDegree.data.Profesionista.primerApellido" class="form-control" required />
				</div>
				<div v-if="editingDegree?.data?.Profesionista" class="mb-3">
					<label class="block mb-2 font-medium" for="segundoApellido">Segundo Apellido:</label>
					<input type="text" v-model="editingDegree.data.Profesionista.segundoApellido" class="form-control" required />
				</div>
				<div v-if="editingDegree?.data?.Profesionista" class="mb-3">
					<label class="block mb-2 font-medium" for="curp">CURP:</label>
					<input type="text" v-model="editingDegree.data.Profesionista.curp" class="form-control" required />
				</div>
				<div v-if="editingDegree?.data?.Carrera" class="mb-3">
					<label class="block mb-2 font-medium" for="nombreCarrera">Carrera:</label>
					<input type="text" v-model="editingDegree.data.Carrera.nombreCarrera" class="form-control" required />
				</div>
			</template>
			<template #footer>
				<button class="btn btn-secondary" @click="showEditModal = false">Cancelar</button>
				<button class="btn btn-primary" @click="submitEdit">Guardar</button>
			</template>
		</BaseModal>

		<DegreeDetailsModal
			v-if="showDegreeDetailsModal && selectedDegree"
			v-model="showDegreeDetailsModal"
			:degree="selectedDegree"
			:test-mode="testMode"
			:institution-id="institutionId"
			:package-id="selectedPackageIndex || selectedDegree.package || 0"
			:is-test-mode="testMode" />
		<CleanErrorModal v-model="showCleanErrorModal" :error-count="errorCount" :test-mode="testMode" @confirm-clean="cleanErrors" />
		<BaseModal v-model="showErrorModal" title="Error" :message="errorMessage" :testMode="false" size="medium">
			<template #body>
				<div class="error-message">
					<div class="error-header">
						<div class="error-icon">
							<i class="fas fa-exclamation-triangle"></i>
						</div>
						<h3 class="error-title">Error</h3>
					</div>
					<p class="error-description">{{ errorMessage }}</p>
				</div>
			</template>
			<template #footer>
				<button class="btn btn-primary" @click="showErrorModal = false">Entendido</button>
			</template>
		</BaseModal>
	</div>
</template>
<style scoped>
.error-description {
	font-size: 1.1rem;
	line-height: 1.6;
	margin-bottom: 1.5rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
	border-left: 4px solid #f71d1d;
}
.error-message {
	background: linear-gradient(135deg, #ecbaba 0%, #e67a7a 100%);
	border: 2px solid #f71d1d;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 32px rgba(254, 178, 178, 0.3);
	animation: slideInError 0.4s ease-out;
}

.error-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
	padding-bottom: 1rem;
	border-bottom: 2px solid #f68484;
}

.error-icon {
	width: 48px;
	height: 48px;
	background: linear-gradient(135deg, #e67a7a 0%, #e67a7a 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 16px rgba(245, 101, 101, 0.4);
}

.error-icon i {
	color: white;
	font-size: 1.5rem;
	animation: pulse 2s infinite;
}

.error-title {
	margin: 0;
	color: #e67a7a;
	font-size: 1.5rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}
.page-container {
	height: calc(100vh - 60px);
	padding: 1rem;
}
.page-container::-webkit-scrollbar {
	width: 8px;
}
.page-container::-webkit-scrollbar-track {
	background: #f1f1f1;
	border-radius: 4px;
}
.page-container::-webkit-scrollbar-thumb {
	background: #888;
	border-radius: 4px;
}
.page-container::-webkit-scrollbar-thumb:hover {
	background: #555;
}

.degree-page-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.75rem;
	flex-wrap: wrap;
	padding: 1rem;
	width: 100%;
}

.degree-page-header__title {
	margin: 0;
	font-size: 1.25rem;
	font-weight: 700;
	text-transform: uppercase;
	line-height: 1.2;
	flex: 1 1 260px;
	min-width: min(100%, 260px);
}

.degree-page-header__actions {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	flex-wrap: wrap;
	gap: 0.5rem;
	flex: 0 1 auto;
	min-width: min(100%, 320px);
	margin-left: auto;
}

.degree-page-header__action {
	flex: 0 0 auto;
}

.degree-page-header__uploader {
	display: inline-flex;
}

/* Asegura que el botón interno del uploader no colapse en ancho */
.degree-page-header__uploader :deep(.btn) {
	white-space: nowrap;
}

@media (max-width: 768px) {
	.degree-page-header {
		align-items: flex-start;
	}
	.degree-page-header__actions {
		justify-content: flex-start;
		width: 100%;
	}
}

@media (max-width: 576px) {
	.degree-page-header__action {
		width: 100%;
	}
	.degree-page-header__actions :deep(.btn),
	.degree-page-header__actions :deep(.va-button) {
		width: 100%;
	}
}

.content-section {
	flex: 1;
	padding: 1rem;
	min-height: 0;
	margin-bottom: 1rem;
	/* overflow-y: auto;  <-- Eliminar esto para que el scroll solo esté en page-container */
}

.tab-content {
	height: 100%;
	min-height: 0;
	padding-bottom: 1rem;
}

/* Hover effects for icons */
.fas {
	transition: color var(--transition-fast);
}

/* Custom styles for form controls */
.form-control[type='file'] {
	padding: var(--spacing-xs);
}

/* Custom styles for checkboxes */
input[type='checkbox'] {
	width: 16px;
	height: 16px;
	cursor: pointer;
}

/* Custom styles for select */
select.form-control {
	cursor: pointer;
}

/* Custom styles for buttons */
.btn i {
	font-size: 0.9em;
}

/* Custom styles for empty states */
.empty-state {
	min-height: 300px;
}

/* Custom styles for search results */
.search-results {
	margin-top: var(--spacing-lg);
}

/* Custom styles for packages tab - sin altura fija para que el scroll del page-container muestre toda la tabla */
.packages-tab-container {
	min-height: 0;
	padding-bottom: 1rem;
}

.packages-content {
	overflow: visible;
	min-height: 0;
	margin-bottom: 120px;
}

/* Filtro por año: diseño responsive (adaptable a pantallas pequeñas) */
.packages-year-filter {
	display: inline-flex;
	width: auto;
	min-width: min(100%, 280px);
	align-items: center;
	gap: 0.5rem;
	padding: 0.5rem 0.875rem;
	background: linear-gradient(135deg, var(--va-primary) 0%, color-mix(in srgb, var(--va-primary) 85%, white) 100%);
	border-radius: 9px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
	margin-bottom: 1rem;
}

.packages-year-label {
	font-size: 1rem;
	font-weight: 600;
	color: white;
	letter-spacing: 0.02em;
	white-space: nowrap;
}

.packages-year-picker {
	--dp-input-padding: 0.35rem 0.6rem;
	--dp-font-size: 0.875rem;
}

.packages-year-picker :deep(.dp__input) {
	min-width: 4rem;
	border-radius: 999px;
	border: none;
	background: rgba(255, 255, 255, 0.95);
	font-weight: 500;
}

.packages-year-info {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 1.5rem;
	height: 1.5rem;
	min-width: 1.5rem;
	min-height: 1.5rem;
	padding: 0;
	border: none;
	border-radius: 50%;
	background: rgba(255, 255, 255, 0.25);
	color: white;
	cursor: pointer;
	transition:
		background 0.2s,
		transform 0.15s;
}

.packages-year-info:hover {
	background: rgba(255, 255, 255, 0.4);
	transform: scale(1.05);
}

/* Responsive: pantallas pequeñas (móviles y tablets) */
@media (max-width: 768px) {
	.packages-year-filter {
		width: 100%;
		min-width: 0;
		gap: 0.75rem;
		padding: 0.75rem 1rem;
	}

	.packages-year-label {
		font-size: 1.05rem;
	}

	.packages-year-picker {
		--dp-input-padding: 0.5rem 0.75rem;
		--dp-font-size: 1rem;
	}

	.packages-year-picker :deep(.dp__input) {
		min-width: 4.5rem;
	}

	.packages-year-info {
		width: 2rem;
		height: 2rem;
		min-width: 2rem;
		min-height: 2rem;
	}
}

@media (max-width: 480px) {
	.packages-year-filter {
		padding: 0.875rem 1rem;
		gap: 0.625rem;
	}

	.packages-year-label {
		font-size: 1rem;
	}
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

/* ===== ESTILOS PARA SECCIÓN DE BÚSQUEDA ===== */

/* Contenedor Principal de Búsqueda */
.search-section {
	padding: 1.5rem;
	background: white;
	border-radius: 12px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
	animation: fadeIn 0.5s ease-in-out;
	margin-bottom: 40px;
}

/* Formulario de Búsqueda */
.search-form {
	margin-bottom: 2rem;
}

.search-controls {
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
	max-width: 600px;
	margin: 0 auto;
}

.search-select {
	width: 100%;
}

.search-label {
	display: block;
	margin-bottom: 0.5rem;
	font-weight: 500;
	color: #1a2a3a;
	font-size: 0.875rem;
}

.search-select-input {
	width: 100%;
	padding: 0.75rem;
	border: 2px solid #e9ecef;
	border-radius: 8px;
	font-size: 0.875rem;
	transition: all 0.3s ease;
}

.search-select-input:focus {
	border-color: #007bff;
	box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
	outline: none;
}

.search-input-group {
	display: flex;
	gap: 1rem;
	align-items: flex-end;
}

.search-input {
	flex: 1;
	padding: 0.75rem;
	border: 2px solid #e9ecef;
	border-radius: 8px;
	font-size: 0.875rem;
	transition: all 0.3s ease;
}

.search-input:focus {
	border-color: #007bff;
	box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
	outline: none;
}

.search-button {
	background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
	color: white;
	transition: all 0.3s ease;
	box-shadow: 0 2px 4px rgba(0, 123, 255, 0.2);
	border: none;
	padding: 0.75rem 1.5rem;
	border-radius: 8px;
	font-weight: 500;
}

.search-button:hover {
	background: linear-gradient(135deg, #0056b3 0%, #004085 100%);
	transform: translateY(-1px);
	box-shadow: 0 4px 8px rgba(0, 123, 255, 0.3);
}

.results-header {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	margin-bottom: 1.5rem;
	padding-bottom: 0.75rem;
	border-bottom: 2px solid #e9ecef;
}

.results-icon {
	color: #007bff;
	font-size: 1.25rem;
}

.results-title {
	font-size: 1.25rem;
	font-weight: 600;
	color: #1a2a3a;
}

.results-grid {
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
}

/* Estados Vacíos */
.initial-state {
	margin-top: 2rem;
}

.empty-icon {
	font-size: 4rem;
	color: #6c757d;
	margin-bottom: 1rem;
}

.empty-title {
	font-size: 1.5rem;
	font-weight: 600;
	color: #1a2a3a;
	margin-bottom: 0.5rem;
}

.empty-description {
	color: #6c757d;
	font-size: 1rem;
	margin: 0;
}

/* Animaciones */
@keyframes fadeIn {
	from {
		opacity: 0;
		transform: scale(0.8);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

/* ===== ESTILOS PARA DEGREE DETAILS ===== */

/* Contenedor Principal */
.degree-details {
	margin-bottom: 1rem;
	background: white;
	border-radius: 12px;
	border: 2px solid #e9ecef;
	transition: all 0.3s ease;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
	animation: fadeIn 0.5s ease-in-out;
	overflow: hidden;
}

.degree-details:hover {
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
	transform: translateY(-2px);
}

/* Header del Título */
.degree-header {
	background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
	padding: 1.5rem;
}

.header-content {
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
}

.student-info {
	display: flex;
	align-items: center;
	gap: 1rem;
}

.student-avatar {
	background: linear-gradient(135deg, #6ab0ff 0%, #0873c4 100%);
	color: white;
	width: 48px;
	height: 48px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}

.student-icon {
	font-size: 1.25rem;
	color: white;
}

.student-details {
	color: white;
}

.student-name {
	font-size: 1.25rem;
	font-weight: 600;
	margin: 0 0 0.25rem 0;
	text-transform: capitalize;
}

.student-career {
	font-size: 0.875rem;
	margin: 0;
	opacity: 0.9;
}

.degree-actions {
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

/* Contenido del Título */
.degree-content {
	padding: 1.5rem;
}

.degree-info {
	display: flex;
	flex-direction: column;
	gap: 1rem;
	margin-bottom: 1.5rem;
}

.info-item {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	padding: 0.75rem;
	background: #f8f9fa;
	border-radius: 8px;
	border-left: 4px solid #007bff;
}

.info-icon {
	color: #007bff;
	font-size: 1.125rem;
	flex-shrink: 0;
}

.info-label {
	font-weight: 600;
	color: #1a2a3a;
	min-width: 120px;
}

.info-value {
	color: #6c757d;
	flex: 1;
}

/* Responsive Design para Búsqueda */
@media (max-width: 768px) {
	.search-section {
		padding: 1rem;
	}

	.search-controls {
		gap: 1rem;
	}

	.search-input-group {
		flex-direction: column;
		align-items: stretch;
	}

	.search-button {
		width: 100%;
	}

	.results-header {
		flex-direction: column;
		align-items: flex-start;
		gap: 0.5rem;
	}

	.results-title {
		font-size: 1.125rem;
	}

	.empty-icon {
		font-size: 3rem;
	}

	.empty-title {
		font-size: 1.25rem;
	}

	/* Responsive para Degree Details */
	.header-content {
		flex-direction: column;
		align-items: flex-start;
		gap: 1rem;
	}

	.student-info {
		width: 100%;
	}

	.degree-actions {
		width: 100%;
		justify-content: flex-end;
	}

	.info-item {
		flex-direction: column;
		align-items: flex-start;
		gap: 0.5rem;
	}

	.info-label {
		min-width: auto;
	}

	.student-avatar {
		width: 40px;
		height: 40px;
	}

	.student-name {
		font-size: 1.125rem;
	}
}

/* ===== ESTILOS PARA CONTADOR DE TÍTULOS ===== */

.counter-card {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
	gap: 1rem;
	align-items: stretch;
}

.counter-item {
	display: flex;
	align-items: center;
	gap: 0.25rem;
	padding: 0.25rem;
	background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
	border-radius: 8px;
	border-left: 4px solid #007bff;
	transition: all 0.3s ease;
}

.counter-item.error {
	display: flex;
	align-items: center;
	gap: 0.25rem;
	padding: 0.25rem;
	background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
	border-radius: 8px;
	border-left: 4px solid #dc3545;
	transition: all 0.3s ease;
	grid-column: 1 / -1; /* El aviso debe ocupar toda la fila en cualquier tamaño */
}

.counter-item:hover {
	transform: translateY(-2px);
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.counter-icon {
	font-size: 2rem;
	flex-shrink: 0;
}

.counter-icon.total {
	color: #007bff;
}

.counter-icon.selected {
	color: #28a745;
}

.counter-icon.error {
	color: #dc3545;
}

.counter-icon.pending {
	color: #ffc107;
}

.counter-content {
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
	min-width: 0;
}

.counter-number {
	font-size: 12px;
	font-weight: 700;
	color: #1a2a3a;
	line-height: 1;
}

.counter-label {
	font-size: 10px;
	color: #6c757d;
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	white-space: normal;
	word-break: break-word;
}

/* Responsive Design para Contador */
@media (max-width: 768px) {
	.counter-card {
		grid-template-columns: repeat(2, minmax(0, 1fr));
		padding: 1rem;
		gap: 0.75rem;
	}

	.counter-item {
		padding: 0.75rem;
		gap: 0.75rem;
	}

	.counter-icon {
		font-size: 1.4rem;
	}

	.counter-number {
		font-size: 12px;
	}

	.counter-label {
		font-size: 0.75rem;
	}
}

@media (max-width: 480px) {
	.counter-card {
		grid-template-columns: 1fr;
	}

	.counter-item {
		justify-content: center;
		text-align: center;
	}

	.counter-item.error {
		justify-content: flex-start;
		text-align: left;
	}
}

/* ===== ESTILOS RESPONSIVOS PARA BOTONES DE ACCIÓN ===== */

.action-buttons-container {
	display: flex;
	flex-wrap: wrap;
	gap: 0.75rem;
	align-items: center;
	flex: 0 1 auto;
	min-width: fit-content;
}

/* Responsive para tablets */
@media (max-width: 1024px) {
	.action-buttons-container {
		gap: 0.5rem;
		font-size: 0.875rem;
		padding: 0.5rem 0.75rem;
	}
}

/* Responsive para móviles */
@media (max-width: 768px) {
	.action-buttons-container {
		flex-direction: column;
		align-items: stretch;
		gap: 0.5rem;
		width: 100%;
		justify-content: center;
		font-size: 0.875rem;
		padding: 0.75rem 1rem;
	}
}

/* Responsive para móviles pequeños */
@media (max-width: 480px) {
	.action-buttons-container {
		gap: 0.375rem;
		font-size: 0.8rem;
		padding: 0.625rem 0.875rem;
	}
}
</style>
